import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { companyApi, uploadFileToSignedUrl } from '../lib/api.ts';
import { CenteredSpinner } from '../components/Spinner.tsx';
import { EmptyState } from '../components/EmptyState.tsx';
import { ErrorMessage } from '../components/ErrorMessage.tsx';
import { Btn } from '../components/Btn.tsx';
import { FileDrop } from '../components/FileDrop.tsx';
import { DateField } from '../components/DateField.tsx';
import { Modal } from '../components/Modal.tsx';
import { expiryState, formatDateOnly, fromDateInput } from '../lib/expiry.ts';
import { uploadDocStats, type UploadDocStats } from '../lib/docStats.ts';
import { complianceFilteredSubmissions, complianceDocName } from '../lib/complianceUploads.ts';
import { DOCUMENT_ACCEPT } from '../lib/fileTypes.ts';
import { downloadSignedUrl } from '../lib/download.ts';
import type { CompanyDocument, Question, QuestionCategory, Submission } from '../lib/types.ts';

export function MyDocuments() {
  const documentsQuery = useQuery({
    queryKey: ['me', 'documents'],
    queryFn: () => companyApi.get<{ documents: CompanyDocument[] }>('/api/me/documents'),
  });
  const submissionsQuery = useQuery({
    queryKey: ['me', 'submissions'],
    queryFn: () => companyApi.get<{ submissions: Submission[] }>('/api/me/submissions'),
  });
  const questionsQuery = useQuery({
    queryKey: ['me', 'questions'],
    queryFn: () => companyApi.get<{ questions: Question[] }>('/api/me/questions'),
  });
  const categoriesQuery = useQuery({
    queryKey: ['me', 'question-categories'],
    queryFn: () =>
      companyApi.get<{ categories: QuestionCategory[] }>('/api/me/question-categories'),
  });

  const documents = documentsQuery.data?.documents ?? [];
  const submissions = submissionsQuery.data?.submissions ?? [];
  const questions = questionsQuery.data?.questions ?? [];
  const categories = categoriesQuery.data?.categories ?? [];

  if (
    documentsQuery.isLoading ||
    submissionsQuery.isLoading ||
    questionsQuery.isLoading ||
    categoriesQuery.isLoading
  ) {
    return <CenteredSpinner />;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900">Compliance Documents</h1>
        <p className="mt-1 text-sm text-slate-500">
          Documents you have uploaded for compliance review.
        </p>
      </div>

      <MyUploads
        submissions={submissions}
        questions={questions}
        categories={categories}
        documents={documents}
      />
    </div>
  );
}

type FileRow = {
  kind: 'file';
  key: string;
  file_name: string;
  created_at: string;
  questionId: string;
  questionLabel: string;
  optionKey: string | null;
  fileId: string;
  expiresAt: string | null;
  mime: string;
  /** The owning question forces an expiry date on every upload (requires_expiry). */
  requiresExpiry: boolean;
};

type UploadRow =
  | FileRow
  | {
      kind: 'adoption';
      key: string;
      file_name: string;
      created_at: string;
      questionId: string;
      questionLabel: string;
      optionKey: string | null;
      documentId: string;
      documentVersion: string | null;
      mime: string;
    };

// Standard documents only carry a storage path, so the preview modal infers
// how to render them from the extension. Uploaded files keep their true mime.
function mimeFromPath(path: string): string {
  const ext = path.split('.').pop()?.toLowerCase() ?? '';
  if (ext === 'pdf') return 'application/pdf';
  if (['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(ext)) {
    return `image/${ext === 'jpg' ? 'jpeg' : ext}`;
  }
  return 'application/octet-stream';
}

function MyUploads({
  submissions,
  questions,
  categories,
  documents,
}: {
  submissions: Submission[];
  questions: Question[];
  categories: QuestionCategory[];
  documents: CompanyDocument[];
}) {
  const [preview, setPreview] = useState<UploadRow | null>(null);
  const [reupload, setReupload] = useState<FileRow | null>(null);
  const questionById = useMemo(() => new Map(questions.map((q) => [q.id, q])), [questions]);
  const docById = useMemo(() => new Map(documents.map((d) => [d.id, d])), [documents]);

  const complianceSubs = useMemo(
    () => complianceFilteredSubmissions(submissions, questionById),
    [submissions, questionById],
  );

  const now = new Date();
  const uploadStats = uploadDocStats(complianceSubs, now);

  // Build per-category lists of uploaded files + adopted standard documents.
  const rowsByCategory = useMemo(() => {
    const map = new Map<string, UploadRow[]>();
    for (const cat of categories) map.set(cat.id, []);

    for (const s of complianceSubs) {
      const q = questionById.get(s.question_id);
      if (!q) continue;
      const bucket = map.get(q.category_id) ?? [];

      for (const f of s.submission_files) {
        bucket.push({
          kind: 'file',
          key: `file:${f.id}`,
          file_name: f.file_name,
          created_at: f.created_at,
          questionId: q.id,
          questionLabel: complianceDocName(q),
          optionKey: f.option_key,
          fileId: f.id,
          expiresAt: f.expires_at,
          mime: f.mime_type,
          requiresExpiry: q.requires_expiry,
        });
      }

      for (const a of s.submission_adoptions) {
        if (a.mode !== 'adopted') continue;
        const doc = docById.get(a.document_id);
        if (!doc) continue;
        bucket.push({
          kind: 'adoption',
          key: `adoption:${a.id}`,
          file_name: doc.name,
          created_at: a.acknowledged_at ?? a.created_at,
          questionId: q.id,
          questionLabel: complianceDocName(q),
          optionKey: a.option_key,
          documentId: doc.id,
          documentVersion: doc.version ?? null,
          mime: mimeFromPath(doc.file_path),
        });
      }

      map.set(q.category_id, bucket);
    }

    for (const [k, list] of map) {
      list.sort((a, b) => a.created_at.localeCompare(b.created_at));
      map.set(k, list);
    }
    return map;
  }, [complianceSubs, questionById, docById, categories]);

  const totalRows = useMemo(
    () => [...rowsByCategory.values()].reduce((n, list) => n + list.length, 0),
    [rowsByCategory],
  );

  if (totalRows === 0) {
    return (
      <EmptyState
        title="No compliant documents yet"
        description="Documents you upload and policies you adopt appear here once your reviewer marks them compliant. Until then, you can manage them in your questionnaire."
      />
    );
  }

  return (
    <div className="space-y-6">
      <SummaryChips stats={uploadStats} />
      {categories.map((cat) => {
        const list = rowsByCategory.get(cat.id) ?? [];
        if (list.length === 0) return null;
        return (
          <section key={cat.id}>
            <div className="mb-2 flex items-baseline justify-between">
              <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                {cat.name}
              </h2>
              <span className="text-xs text-slate-500">
                {list.length} item{list.length === 1 ? '' : 's'}
              </span>
            </div>
            <div className="space-y-2">
              {list.map((row) => {
                const state =
                  row.kind === 'file' ? expiryState({ expires_at: row.expiresAt }, now) : null;
                const border =
                  state === 'expired'
                    ? 'border-red-300'
                    : state === 'expiring'
                      ? 'border-amber-300'
                      : 'border-slate-200';
                return (
                  <div key={row.key} className={`rounded-xl border bg-white p-4 ${border}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div
                          className="truncate text-sm font-semibold"
                          style={{ color: 'var(--brand-primary)' }}
                        >
                          {row.file_name}
                        </div>
                        <div className="mt-0.5 truncate text-xs text-slate-500">
                          {row.questionLabel}
                          {row.optionKey ? ` · ${row.optionKey}` : ''}
                          {row.kind === 'adoption' && row.documentVersion
                            ? ` · v${row.documentVersion}`
                            : ''}
                        </div>
                      </div>
                      {row.kind === 'adoption' ? (
                        <span className="shrink-0 rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700">
                          Adopted
                        </span>
                      ) : (
                        <UploadStateChip expiresAt={row.expiresAt} now={now} />
                      )}
                    </div>

                    <div className="mt-3 grid grid-cols-2 gap-3 border-t border-slate-100 pt-3">
                      <div>
                        <div className="text-[11px] text-slate-400">Uploaded</div>
                        <div className="text-xs font-medium text-slate-700">
                          {new Date(row.created_at).toLocaleDateString()}
                        </div>
                      </div>
                      <div>
                        <div className="text-[11px] text-slate-400">Expiry</div>
                        <div
                          className={`text-xs font-medium ${
                            state === 'expired'
                              ? 'text-red-600'
                              : state === 'expiring'
                                ? 'text-amber-600'
                                : 'text-slate-700'
                          }`}
                        >
                          {row.kind === 'file' && row.expiresAt
                            ? formatDateOnly(row.expiresAt)
                            : '—'}
                        </div>
                      </div>
                    </div>

                    <div className="mt-3 flex gap-2">
                      <Btn
                        variant="secondary"
                        size="sm"
                        className="flex-1"
                        onClick={() => setPreview(row)}
                      >
                        View File
                      </Btn>
                      {row.kind === 'file' && (
                        <Btn size="sm" className="flex-1" onClick={() => setReupload(row)}>
                          Re-upload
                        </Btn>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        );
      })}

      {preview && <UploadPreviewModal row={preview} onClose={() => setPreview(null)} />}
      {reupload && <ReuploadModal row={reupload} onClose={() => setReupload(null)} />}
    </div>
  );
}

function SummaryChips({ stats }: { stats: UploadDocStats }) {
  const cards = [
    { label: 'Current', value: stats.current, text: 'text-emerald-600' },
    { label: 'Expiring', value: stats.expiring, text: 'text-amber-600' },
    { label: 'Expired', value: stats.expired, text: 'text-red-600' },
  ];
  return (
    <div className="grid grid-cols-3 gap-3">
      {cards.map((c) => {
        const box =
          c.label === 'Current' && c.value > 0
            ? 'border-emerald-200 bg-emerald-50'
            : c.label === 'Expiring' && c.value > 0
              ? 'border-amber-300 bg-amber-50'
              : c.label === 'Expired' && c.value > 0
                ? 'border-red-200 bg-red-50'
                : 'border-slate-200 bg-white';
        return (
          <div key={c.label} className={`rounded-xl border p-4 text-center ${box}`}>
            <div className={`text-xl font-bold ${c.text}`}>{c.value}</div>
            <div className="text-xs text-slate-500">{c.label}</div>
          </div>
        );
      })}
    </div>
  );
}

// State-only chip (the dates render in the card's meta row, so unlike
// ExpiryChip this shows just the health label).
function UploadStateChip({ expiresAt, now }: { expiresAt: string | null; now: Date }) {
  const state = expiryState({ expires_at: expiresAt }, now);
  if (state === 'expired') {
    return (
      <span className="shrink-0 rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700">
        Expired
      </span>
    );
  }
  if (state === 'expiring') {
    return (
      <span className="shrink-0 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
        ⚠ Expiring Soon
      </span>
    );
  }
  return (
    <span className="shrink-0 rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700">
      ✓ Current
    </span>
  );
}

// Preview an uploaded or adopted document in a modal: images inline, PDFs in
// an iframe, and an open-in-new-tab fallback for anything else. Mirrors the
// admin FilePreviewModal but against the vendor's /api/me endpoints.
function UploadPreviewModal({ row, onClose }: { row: UploadRow; onClose: () => void }) {
  const path =
    row.kind === 'file'
      ? `/api/me/submissions/${row.questionId}/files/${row.fileId}/download-url`
      : `/api/me/documents/${row.documentId}/download-url`;
  const urlQuery = useQuery({
    queryKey: ['me', 'file-preview', row.key],
    queryFn: () => companyApi.get<{ url: string }>(path),
    staleTime: 4 * 60 * 1000,
  });
  const url = urlQuery.data?.url;
  const isImage = row.mime.startsWith('image/');
  const isPdf = row.mime === 'application/pdf';

  function openInNewTab() {
    if (url) window.open(url, '_blank', 'noopener');
  }

  return (
    <Modal
      open
      title={row.file_name}
      size="lg"
      onClose={onClose}
      footer={
        <>
          <Btn
            size="sm"
            variant="ghost"
            disabled={!url}
            onClick={() => url && downloadSignedUrl(url, row.file_name)}
          >
            Download file
          </Btn>
          <Btn size="sm" variant="ghost" disabled={!url} onClick={openInNewTab}>
            Open in new tab
          </Btn>
          <Btn size="sm" variant="secondary" onClick={onClose}>
            Close
          </Btn>
        </>
      }
    >
      {!url ? (
        <div className="flex h-40 items-center justify-center text-sm text-slate-400">
          {urlQuery.isError ? 'File unavailable' : 'Loading…'}
        </div>
      ) : isImage ? (
        <img src={url} alt={row.file_name} className="mx-auto max-h-[65vh] max-w-full rounded-md" />
      ) : isPdf ? (
        <iframe
          src={url}
          title={row.file_name}
          className="h-[65vh] w-full rounded-md border border-slate-200"
        />
      ) : (
        <div className="flex h-40 flex-col items-center justify-center gap-2 text-sm text-slate-500">
          <span>Preview not available for this file type.</span>
          <Btn size="sm" variant="secondary" onClick={openInNewTab}>
            Open in new tab
          </Btn>
        </div>
      )}
    </Modal>
  );
}

// Replace an uploaded file: pick a new file, optionally set its expiry date,
// then upload + register it and supersede the old version (the same soft
// delete the wizard's Remove uses, so the admin file history keeps the
// replaced version).
function ReuploadModal({ row, onClose }: { row: FileRow; onClose: () => void }) {
  const qc = useQueryClient();
  const [file, setFile] = useState<File | null>(null);
  const [expiry, setExpiry] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  // Questions flagged requires_expiry must never register a dateless upload —
  // mirrors the wizard's compose step and the comment path's server rule.
  const missingExpiry = row.requiresExpiry && !expiry;

  async function upload() {
    if (!file || busy) return;
    if (missingExpiry) {
      setError(new Error('This document requires an expiry date.'));
      return;
    }
    setError(null);
    setBusy(true);
    try {
      const mime = file.type || 'application/octet-stream';
      const presigned = await companyApi.post<{ path: string; url: string }>(
        `/api/me/submissions/${row.questionId}/upload-url`,
        { file_name: file.name, mime_type: mime },
      );
      await uploadFileToSignedUrl(presigned.url, file);
      await companyApi.post(`/api/me/submissions/${row.questionId}/files`, {
        file_path: presigned.path,
        file_name: file.name,
        size_bytes: file.size,
        mime_type: mime,
        option_key: row.optionKey ?? undefined,
        expires_at: fromDateInput(expiry) ?? undefined,
      });
      // Retire the replaced version only after the new one is registered.
      await companyApi.delete(`/api/me/submissions/${row.questionId}/files/${row.fileId}`);
      await qc.invalidateQueries({ queryKey: ['me', 'submissions'] });
      await qc.invalidateQueries({ queryKey: ['me'] });
      onClose();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open
      title={`Re-upload — ${row.file_name}`}
      onClose={onClose}
      footer={
        <>
          <Btn size="sm" variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Btn>
          <Btn size="sm" onClick={() => void upload()} disabled={!file || missingExpiry} loading={busy}>
            Upload & replace
          </Btn>
        </>
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-slate-500">
          The new file replaces the current one for “{row.questionLabel}
          {row.optionKey ? ` · ${row.optionKey}` : ''}”.
        </p>
        <FileDrop
          multiple={false}
          accept={DOCUMENT_ACCEPT}
          disabled={busy}
          onFiles={(list) => setFile(list.item(0))}
        />
        {file && (
          <div className="rounded-md bg-slate-50 px-3 py-2 text-sm text-slate-700">
            📎 {file.name}
          </div>
        )}
        <div className="text-sm">
          <span className="font-medium text-slate-700">
            Expiry date
            {row.requiresExpiry && <span className="ml-0.5 text-red-600">*</span>}
          </span>
          <div className="mt-1">
            <DateField
              value={expiry}
              onChange={setExpiry}
              disabled={busy}
              ariaLabel="Expiry date"
              placeholder={row.requiresExpiry ? 'Expiry date required' : 'No expiry'}
              invalid={missingExpiry && !!file}
            />
          </div>
          {row.requiresExpiry ? (
            <span
              className={`mt-1 block text-xs ${missingExpiry && file ? 'font-medium text-red-600' : 'text-slate-400'}`}
            >
              This document requires an expiry date.
            </span>
          ) : (
            <span className="mt-1 block text-xs text-slate-400">
              Leave blank if this document does not expire.
            </span>
          )}
        </div>
        <ErrorMessage error={error} />
      </div>
    </Modal>
  );
}
